import { useState, useEffect, useRef } from 'react';
import { ScrollReveal } from './ScrollReveal';
import { Atom, Cpu, Shield, Zap, Globe, Lock, ArrowRight, Network } from 'lucide-react';

const QUANTUM_FEATURES = [
  { icon: Atom, title: 'Quantum-Ready Architecture', desc: 'Our agent orchestration layer is designed with forward-compatible cryptographic protocols and modular architecture that can integrate post-quantum encryption standards as they mature.' },
  { icon: Shield, title: 'Post-Quantum Cryptography', desc: 'Prepared for NIST-approved post-quantum cryptographic algorithms. When quantum computing advances, your agent infrastructure transitions seamlessly — without architectural debt.' },
  { icon: Cpu, title: 'Hybrid Compute Readiness', desc: 'Agent workloads are designed to leverage hybrid classical-quantum compute environments as they become commercially available — enabling exponentially faster pattern recognition and optimization.' },
  { icon: Network, title: 'Quantum-Enhanced Intelligence', desc: 'Future integration pathways for quantum machine learning models — enabling agent reasoning capabilities that surpass classical AI limitations for complex decision-support.' },
  { icon: Lock, title: 'Quantum Key Distribution Ready', desc: 'Architecture supports future QKD integration for ultra-secure communication between agent nodes, executive layers, and external systems — unhackable by design.' },
  { icon: Globe, title: 'Future-Proof by Philosophy', desc: 'Every architectural decision is made with a 10-year horizon. Aurocle Agents is not built for today\'s technology alone — it is built for the technology that will define the next era of enterprise intelligence.' },
];

function QuantumParticle({ delay }) {
  return (
    <div className="absolute w-1 h-1 rounded-full bg-cyan-400/60"
      style={{
        left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
        animation: `float ${4 + Math.random() * 4}s ease-in-out infinite ${delay}s, glow-pulse ${3 + Math.random() * 2}s ease-in-out infinite ${delay}s`,
      }} />
  );
}

function QuantumOrbit() {
  return (
    <div className="relative w-48 h-48 sm:w-64 sm:h-64 mx-auto">
      {/* Center nucleus */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-cyan-400/20 border border-cyan-400/40 animate-glow-pulse flex items-center justify-center">
        <div className="w-2 h-2 rounded-full bg-cyan-400" />
      </div>
      {/* Orbit rings */}
      {[80, 120, 160].map((size, i) => (
        <div key={i} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-cyan-400/10"
          style={{ width: size, height: size, animation: `spin ${20 + i * 10}s linear infinite ${i % 2 === 0 ? '' : 'reverse'}` }}>
          <div className="absolute w-1.5 h-1.5 rounded-full bg-cyan-400/60 -top-0.5 left-1/2 -translate-x-1/2" style={{ animation: `glow-pulse ${2 + i}s ease-in-out infinite` }} />
          <div className="absolute w-1 h-1 rounded-full bg-aurocle-gold/60 top-1/2 -right-0.5 -translate-y-1/2" style={{ animation: `glow-pulse ${3 + i}s ease-in-out infinite 1s` }} />
        </div>
      ))}
      {/* Entanglement lines */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200">
        <line x1="60" y1="60" x2="140" y2="140" stroke="rgba(0,200,255,0.08)" strokeWidth="0.5" className="animate-glow-pulse" />
        <line x1="140" y1="60" x2="60" y2="140" stroke="rgba(212,175,55,0.08)" strokeWidth="0.5" className="animate-glow-pulse-delay" />
      </svg>
    </div>
  );
}

export default function QuantumReady() {
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setActiveIdx(i => (i + 1) % QUANTUM_FEATURES.length), 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative py-24 sm:py-32 overflow-hidden" data-testid="quantum-ready-section">
      {/* Quantum background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-aurocle-base via-[#030508] to-aurocle-base" />
        <div className="absolute inset-0" style={{background: 'radial-gradient(ellipse at 30% 50%, rgba(0,200,255,0.03) 0%, transparent 60%)'}} />
        <div className="absolute inset-0" style={{background: 'radial-gradient(ellipse at 70% 50%, rgba(212,175,55,0.02) 0%, transparent 60%)'}} />
        {/* Quantum particles */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({length: 20}).map((_, i) => <QuantumParticle key={i} delay={i * 0.3} />)}
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <div>
            <ScrollReveal>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-cyan-400/20 bg-cyan-400/5 mb-6">
                <Atom size={12} className="text-cyan-400" />
                <span className="text-[10px] font-mono uppercase tracking-wider text-cyan-400">Quantum-Ready Infrastructure</span>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-aurocle-text mb-4">
                Built for today.{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-aurocle-gold">Engineered for tomorrow.</span>
              </h2>
              <p className="text-base text-aurocle-platinum-muted leading-relaxed mb-8">
                While others build for current technology alone, Aurocle Agents is architecturally prepared for the quantum computing era — with forward-compatible security, modular compute layers, and integration pathways for quantum-enhanced AI capabilities.
              </p>
            </ScrollReveal>

            <div className="space-y-3">
              {QUANTUM_FEATURES.map((feat, i) => {
                const Icon = feat.icon;
                const isActive = activeIdx === i;
                return (
                  <ScrollReveal key={i} delay={i * 60}>
                    <div
                      onClick={() => setActiveIdx(i)}
                      className={`p-4 cursor-pointer transition-all duration-500 border ${
                        isActive
                          ? 'bg-gradient-to-r from-cyan-400/5 to-transparent border-cyan-400/20 shadow-[0_0_20px_rgba(0,200,255,0.05)]'
                          : 'border-white/4 hover:border-white/10'
                      }`}
                      data-testid={`quantum-feature-${i}`}
                    >
                      <div className="flex items-start gap-3">
                        <Icon size={16} className={`mt-0.5 shrink-0 transition-colors ${isActive ? 'text-cyan-400' : 'text-aurocle-text-secondary'}`} />
                        <div>
                          <h3 className={`text-sm font-heading font-medium transition-colors ${isActive ? 'text-aurocle-text' : 'text-aurocle-text-secondary'}`}>{feat.title}</h3>
                          {isActive && <p className="text-xs text-aurocle-platinum-muted leading-relaxed mt-2">{feat.desc}</p>}
                        </div>
                      </div>
                    </div>
                  </ScrollReveal>
                );
              })}
            </div>
          </div>

          {/* Right: Quantum visualization */}
          <ScrollReveal delay={200}>
            <div className="relative">
              <div className="absolute -top-16 -right-16 w-80 h-80 bg-cyan-400/5 rounded-full blur-[120px] pointer-events-none" />
              <div className="absolute -bottom-16 -left-16 w-60 h-60 bg-aurocle-gold/5 rounded-full blur-[100px] pointer-events-none" />

              <QuantumOrbit />

              <div className="mt-10 grid grid-cols-2 gap-3">
                {[
                  { label: 'Post-Quantum Crypto', status: 'Ready' },
                  { label: 'Hybrid Compute', status: 'Prepared' },
                  { label: 'QKD Integration', status: 'Pathway' },
                  { label: 'Quantum ML', status: 'Roadmap' },
                ].map((item, i) => (
                  <div key={i} className="p-3 border border-cyan-400/10 bg-cyan-400/[0.02] text-center">
                    <div className="text-[10px] font-mono text-cyan-400/60 uppercase tracking-wider">{item.label}</div>
                    <div className="text-xs text-aurocle-text mt-1 font-medium">{item.status}</div>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>

        <ScrollReveal delay={400}>
          <div className="mt-16 text-center">
            <div className="glass-card p-8 border-cyan-400/10 max-w-2xl mx-auto relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/[0.02] to-aurocle-gold/[0.02]" />
              <div className="relative">
                <p className="text-sm text-aurocle-text leading-relaxed mb-6">
                  The organizations that invest in quantum-ready infrastructure today will lead in the post-quantum era. Aurocle Agents ensures your private workforce architecture transitions seamlessly — no rebuild, no migration debt, no security gaps.
                </p>
                <a href="#contact" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-400/20 to-aurocle-gold/20 border border-cyan-400/20 text-sm text-aurocle-text hover:border-cyan-400/40 transition-all" data-testid="quantum-cta">
                  Discuss Future-Ready Architecture <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>

      <style>{`
        @keyframes spin { from { transform: translate(-50%, -50%) rotate(0deg); } to { transform: translate(-50%, -50%) rotate(360deg); } }
      `}</style>
    </section>
  );
}
